import { useEffect, useRef } from "react";

type Particle = {
  x: number;
  y: number;
  ox: number;
  oy: number;
  vx: number;
  vy: number;
  size: number;
  brightness: number;
  returnSpeed: number;
};

const PARTICLE_COUNT = 3500;
const INFLUENCE = 140;

export default function SandblastCanvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLCanvasElement>(null);
  const noiseRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const main = mainRef.current;
    if (!container || !main) return;

    const ctx = main.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Pre-generate a tileable noise texture (once)
    if (!noiseRef.current) {
      const N = 180;
      const nc = document.createElement("canvas");
      nc.width = N;
      nc.height = N;
      const nctx = nc.getContext("2d");
      if (nctx) {
        const img = nctx.createImageData(N, N);
        for (let i = 0; i < img.data.length; i += 4) {
          const v = Math.floor(Math.random() * 255);
          img.data[i] = v;
          img.data[i + 1] = v;
          img.data[i + 2] = v;
          img.data[i + 3] = 18; // very subtle
        }
        nctx.putImageData(img, 0, 0);
      }
      noiseRef.current = nc;
    }
    let cw = 0;
    let ch = 0;
    let prevCw = 0;
    let prevCh = 0;

    const particles: Particle[] = [];

    const initParticles = () => {
      particles.length = 0;
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const x = Math.random() * cw;
        const y = Math.random() * ch;
        particles.push({
          x,
          y,
          ox: x,
          oy: y,
          vx: 0,
          vy: 0,
          size: 0.8 + Math.random() * 1.2,
          brightness: 0.3 + Math.random() * 0.7,
          returnSpeed: 0.04 + Math.random() * 0.03,
        });
      }
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      const newCw = rect.width;
      const newCh = rect.height;
      main.width = Math.floor(newCw * dpr);
      main.height = Math.floor(newCh * dpr);
      main.style.width = `${newCw}px`;
      main.style.height = `${newCh}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (particles.length === 0) {
        cw = newCw;
        ch = newCh;
        initParticles();
      } else {
        // Rescale origins proportionally, keep velocity
        const sx = prevCw > 0 ? newCw / prevCw : 1;
        const sy = prevCh > 0 ? newCh / prevCh : 1;
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          p.ox *= sx;
          p.oy *= sy;
          p.x *= sx;
          p.y *= sy;
        }
        cw = newCw;
        ch = newCh;
      }
      prevCw = cw;
      prevCh = ch;
    };
    resize();

    const mouse = { x: cw / 2, y: ch / 2, hasReal: false };
    const prevMouse = { x: mouse.x, y: mouse.y };
    let lastMoveTs = 0;
    let autoT = 0;
    let mouseSpeed = 0;

    const onMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.hasReal = true;
      lastMoveTs = performance.now();
    };
    const onLeave = () => {
      mouse.hasReal = false;
    };

    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);
    window.addEventListener("resize", resize);

    let raf = 0;

    const draw = () => {
      // Clean clear — deep slate-blue industrial base
      ctx.fillStyle = "#0F1B2E";
      ctx.fillRect(0, 0, cw, ch);

      // Subtle metallic grain overlay
      const noise = noiseRef.current;
      if (noise) {
        const pattern = ctx.createPattern(noise, "repeat");
        if (pattern) {
          ctx.save();
          ctx.globalAlpha = 0.55;
          ctx.fillStyle = pattern;
          ctx.fillRect(0, 0, cw, ch);
          ctx.restore();
        }
      }

      // Determine active nozzle position (real mouse or auto Lissajous)
      const now = performance.now();
      const idle = !mouse.hasReal || now - lastMoveTs > 800;
      let mx: number;
      let my: number;
      let activeSpeed: number;

      if (idle) {
        autoT += 0.006;
        mx = cw * 0.5 + Math.sin(autoT) * cw * 0.28;
        my = ch * 0.5 + Math.sin(autoT * 1.6) * ch * 0.18;
        activeSpeed = 3;
        prevMouse.x = mx;
        prevMouse.y = my;
      } else {
        mx = mouse.x;
        my = mouse.y;
        mouseSpeed = Math.hypot(mx - prevMouse.x, my - prevMouse.y);
        activeSpeed = mouseSpeed;
        prevMouse.x = mx;
        prevMouse.y = my;
      }

      const speedBoost = 1 + Math.min(Math.max(activeSpeed / 15, 0), 2.5);

      // Particle update + draw in single loop
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const p = particles[i];
        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.hypot(dx, dy);

        if (dist < INFLUENCE && dist > 0) {
          const t = 1 - dist / INFLUENCE;
          const force = t * t * 5.5 * speedBoost;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        }

        // Spring back to origin
        p.vx += (p.ox - p.x) * p.returnSpeed;
        p.vy += (p.oy - p.y) * p.returnSpeed;

        // Damping
        p.vx *= 0.82;
        p.vy *= 0.82;

        p.x += p.vx;
        p.y += p.vy;

        let alpha: number;
        let r = 245;
        let g = 242;
        let b = 237;
        if (dist < INFLUENCE) {
          const edgeFactor = dist / INFLUENCE;
          const glow = 0.4 + edgeFactor * 0.6;
          alpha = p.brightness * glow * (0.3 + edgeFactor * 0.7);
          r = 255;
          g = 252;
          b = 247;
        } else {
          alpha = p.brightness * 0.55;
        }

        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      // Blast-zone ring when moving fast
      if (activeSpeed > 2) {
        const ringAlpha = Math.min(Math.max(activeSpeed / 20, 0), 1) * 0.06;
        if (ringAlpha > 0) {
          ctx.strokeStyle = `rgba(255,252,247,${ringAlpha})`;
          ctx.lineWidth = 8;
          ctx.beginPath();
          ctx.arc(mx, my, 130, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      // Custom cursor / nozzle HUD
      ctx.save();
      ctx.globalCompositeOperation = "source-over";

      // Outer ghost ring (influence radius hint)
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(mx, my, INFLUENCE, 0, Math.PI * 2);
      ctx.stroke();

      // Crosshair NSEW
      ctx.strokeStyle = "rgba(255,255,255,0.55)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(mx, my - 24);
      ctx.lineTo(mx, my - 16);
      ctx.moveTo(mx, my + 16);
      ctx.lineTo(mx, my + 24);
      ctx.moveTo(mx - 24, my);
      ctx.lineTo(mx - 16, my);
      ctx.moveTo(mx + 16, my);
      ctx.lineTo(mx + 24, my);
      ctx.stroke();

      // Center dot
      ctx.fillStyle = "rgba(255,255,255,0.8)";
      ctx.beginPath();
      ctx.arc(mx, my, 2, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 overflow-hidden"
      style={{ backgroundColor: "#0F1B2E", cursor: "none" }}
      aria-hidden="true"
    >
      <canvas ref={mainRef} className="block w-full h-full" />
    </div>
  );
}